import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FiMail, FiPaperclip, FiDownload, FiPrinter, FiImage, FiAlertCircle, FiEye } from 'react-icons/fi';
import Loadingx from '../Loadingx/Loadingx';
import EmlPreview, { canPreview } from './EmlPreview';
import { parseEml, formatSize } from '../../libs/eml/emlParser';
import {
  SANDBOX,
  buildEmlDocument,
  createInlineUrls,
  resolveCidImages,
} from '../../libs/eml/emlFrame';
import './Viewerstyles.css';
import './Emlstyles.css';

/**
 * Visor en linea de correos electronicos .eml
 * @param {object} props emlUrl, showPrint y showDownload
 */
function EMLViewer({ emlUrl, showPrint, showDownload }) {
  const fileName = decodeURIComponent(String(emlUrl || '').split('/').pop());
  const [email, setEmail] = useState(null);
  const [rawBytes, setRawBytes] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [allowRemote, setAllowRemote] = useState(false);
  const [showHeaders, setShowHeaders] = useState(false);
  //Adjunto que se esta previsualizando, null cuando el visor muestra el correo
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setEmail(null);
    setErrorMessage('');
    setPreview(null);
    fetch(emlUrl, { method: 'GET', mode: 'cors', cache: 'no-cache' })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`No se pudo descargar el correo (${response.status})`);
        }
        return response.arrayBuffer();
      })
      .then((buffer) => {
        if (cancelled) {
          return;
        }
        setRawBytes(new Uint8Array(buffer));
        setEmail(parseEml(buffer));
      })
      .catch((error) => {
        if (!cancelled) {
          setErrorMessage(error.message || 'No se pudo abrir el correo electronico.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [emlUrl]);

  //Genero las urls locales de las imagenes incrustadas para reemplazar las referencias cid:
  const inlineUrls = useMemo(() => (email ? createInlineUrls(email.attachments) : {}), [email]);

  useEffect(() => {
    return () => {
      Object.values(inlineUrls).forEach((url) => URL.revokeObjectURL(url));
    };
  }, [inlineUrls]);

  /**
   * Reemplaza las referencias cid: del html por las urls locales
   * @param {string} html cuerpo html del correo
   * @returns {string} html con las imagenes incrustadas resueltas
   */
  const replaceInlineImages = useCallback((html) => resolveCidImages(html, inlineUrls), [inlineUrls]);

  /**
   * Construye el documento que se renderiza dentro del iframe aislado
   * @returns {string} documento html listo para el atributo srcDoc
   */
  const buildFrameDocument = useCallback(() => {
    if (!email) {
      return '';
    }
    return buildEmlDocument({
      html: email.html ? replaceInlineImages(email.html) : null,
      text: email.text,
      allowRemote,
    });
  }, [email, allowRemote, replaceInlineImages]);

  /**
   * Descarga un contenido como archivo local
   * @param {Blob} blob contenido a descargar
   * @param {string} name nombre del archivo
   * @returns {void}
   */
  const downloadBlob = (blob, name) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.style.display = 'none';
    link.href = url;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  /**
   * Descarga el correo completo en formato .eml
   * @returns {void}
   */
  const downloadEmail = () => {
    if (!showDownload || !rawBytes) {
      return;
    }
    downloadBlob(new Blob([rawBytes], { type: 'message/rfc822' }), fileName);
  };

  /**
   * Descarga un adjunto del correo
   * @param {object} attachment adjunto seleccionado
   * @returns {void}
   */
  const downloadAttachment = (attachment) => {
    if (!showDownload) {
      return;
    }
    downloadBlob(new Blob([attachment.bytes], { type: attachment.contentType }), attachment.filename);
  };

  /**
   * Imprime el correo abriendo una ventana con las cabeceras y el cuerpo
   * @returns {void}
   */
  const printEmail = () => {
    if (!showPrint || !email) {
      return;
    }
    const openWindow = window.open('', '_blank');
    if (!openWindow) {
      return;
    }
    openWindow.document.open();
    openWindow.document.write(buildFrameDocument());
    openWindow.document.title = email.headers.subject || fileName;
    openWindow.document.close();

    /**
     * Lanza el dialogo de impresion de la ventana auxiliar
     * @returns {void}
     */
    const launchPrint = () => {
      openWindow.focus();
      openWindow.print();
    };
    //Espero a que se pinten las imagenes incrustadas antes de imprimir
    if (openWindow.document.readyState === 'complete') {
      launchPrint();
      return;
    }
    openWindow.onload = launchPrint;
  };

  useEffect(() => {
    /**
     * Bloquea la impresion y el guardado por teclado cuando no hay permisos
     * @param {KeyboardEvent} event evento de teclado
     * @returns {void}
     */
    const handleKeyDown = (event) => {
      if (event.ctrlKey && (event.key === 'p' || event.key === 'P')) {
        event.preventDefault();
        if (showPrint) {
          printEmail();
        } else {
          alert('Usuario No tiene Permisos para Imprimir');
        }
      }
      if (event.ctrlKey && (event.key === 's' || event.key === 'S')) {
        event.preventDefault();
      }
    };

    /**
     * Deshabilita el menu contextual del visor
     * @param {MouseEvent} event evento del mouse
     * @returns {void}
     */
    const handleContextMenu = (event) => {
      event.preventDefault();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('contextmenu', handleContextMenu);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('contextmenu', handleContextMenu);
    };
    // eslint-disable-next-line
  }, [showPrint, email, allowRemote]);

  if (errorMessage !== '') {
    return (
      <div className="eml-error">
        <FiAlertCircle size={28} />
        &nbsp;{errorMessage}
      </div>
    );
  }

  if (!email) {
    return <Loadingx />;
  }

  const attachments = email.attachments.filter((item) => !item.inline || item.contentId === '');
  const hasRemoteContent = email.html !== null && /(src|url\()\s*=?\s*["'(]?https?:/i.test(email.html);

  return (
    <div className="eml-viewer-container">
      <div className="top-bar">
        <div className="eml-top-bar">
          <div className="eml-title">
            <FiMail size={28} style={{ marginRight: '8px', color: '#fd7e14' }} />
            <span title={email.headers.subject || fileName}>{email.headers.subject || fileName}</span>
          </div>
          <div className="eml-actions">
            {hasRemoteContent && !allowRemote && (
              <FiImage
                size={26}
                className="eml-icon"
                title="mostrar contenido remoto"
                onClick={() => setAllowRemote(true)}
              />
            )}
            {showDownload && (
              <FiDownload size={26} className="eml-icon" title="descargar correo" onClick={downloadEmail} />
            )}
            {showPrint && <FiPrinter size={26} className="eml-icon" title="imprimir" onClick={printEmail} />}
          </div>
        </div>
      </div>

      <div className="eml-metadata">
        <div>
          <b>De:</b> {email.headers.from || '(sin remitente)'}
        </div>
        <div>
          <b>Para:</b> {email.headers.to || '(sin destinatario)'}
        </div>
        {email.headers.cc !== '' && (
          <div>
            <b>CC:</b> {email.headers.cc}
          </div>
        )}
        <div>
          <b>Fecha:</b> {email.headers.date || '(sin fecha)'}
        </div>
        <button type="button" className="eml-link-button" onClick={() => setShowHeaders(!showHeaders)}>
          {showHeaders ? 'Ocultar cabeceras tecnicas' : 'Ver cabeceras tecnicas'}
        </button>
        {showHeaders && (
          <div className="eml-headers">
            {email.headerList.map((header, index) => (
              <div key={`${header.name}-${index}`}>
                <b>{header.name}:</b> {header.value}
              </div>
            ))}
          </div>
        )}
      </div>

      {attachments.length > 0 && (
        <div className="eml-attachments">
          <div className="eml-attachments-title">
            <FiPaperclip size={18} />
            &nbsp;Adjuntos ({attachments.length})
          </div>
          {attachments.map((attachment, index) => (
            <div className="eml-attachment" key={`${attachment.filename}-${index}`}>
              <span className="eml-attachment-name" title={attachment.contentType}>
                {attachment.filename}
              </span>
              <span className="eml-attachment-size">{formatSize(attachment.size)}</span>
              {canPreview(attachment) && (
                <FiEye
                  size={20}
                  className="eml-icon"
                  title="previsualizar adjunto"
                  onClick={() => setPreview(attachment)}
                />
              )}
              {showDownload ? (
                <FiDownload
                  size={20}
                  className="eml-icon"
                  title="descargar adjunto"
                  onClick={() => downloadAttachment(attachment)}
                />
              ) : (
                <span className="eml-attachment-blocked" title="Usuario No tiene Permisos para Descargar">
                  sin permiso de descarga
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      <iframe
        className="eml-body"
        title={email.headers.subject || fileName}
        sandbox={SANDBOX}
        srcDoc={buildFrameDocument()}
      />

      {preview !== null && (
        <EmlPreview
          attachment={preview}
          showDownload={showDownload}
          onDownload={downloadAttachment}
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  );
}

export default EMLViewer;
